import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Squares2X2Icon,
  PlusIcon,
  ListBulletIcon,
  ClockIcon,
  LanguageIcon,
  StarIcon,
  BookOpenIcon,
} from "@heroicons/react/16/solid"

import { DashboardLayout } from "../../layouts/DashboardLayout"
import {
  AdminCourseItem,
  listAdminCoursesApi,
} from "../../features/courses/api/course.api"

const LIMIT = 6

/* ─── helpers ─────────────────────────────────────────── */

const withHttps = (url?: string) => {
  if (!url) return null
  return url.startsWith("http") ? url : `https://${url}`
}

const PriceBadge = ({
  type,
  amount,
}: {
  type: "free" | "paid" | "mixed"
  amount: number
}) => {
  if (type === "free")
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
        Бесплатно
      </span>
    )
  if (type === "mixed")
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 ring-1 ring-purple-200">
        От ${amount}
      </span>
    )
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 ring-1 ring-blue-200">
      ${amount}
    </span>
  )
}

const PromotedBadge = () => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 ring-1 ring-amber-200">
    <StarIcon className="w-3 h-3" />
    Продвигается
  </span>
)

const InactiveBadge = () => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 ring-1 ring-gray-200">
    Неактивен
  </span>
)

const CoursePlaceholder = () => (
  <div className="w-full h-full bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
    <BookOpenIcon className="w-8 h-8 text-blue-200" />
  </div>
)

/* ─── main ─────────────────────────────────────────────── */

export const CoursePage = () => {
  const navigate = useNavigate()

  const [courses, setCourses] = useState<AdminCourseItem[]>([])
  const [view, setView] = useState<"grid" | "list">("grid")
  const [offset, setOffset] = useState(0)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setLoading(true)
    listAdminCoursesApi(LIMIT, offset)
      .then((res) => {
        setCourses(res.data.items)
        setTotal(res.data.total)
      })
      .finally(() => setLoading(false))
  }, [offset])

  const totalPages = Math.ceil(total / LIMIT)
  const currentPage = Math.floor(offset / LIMIT) + 1

  return (
    <DashboardLayout title="Курсы">
      <div className="bg-white rounded-xl border overflow-hidden">

        {/* HEADER */}
        <div className="flex items-center justify-between px-5 py-4 border-b bg-gray-50/60">
          <div className="flex items-center gap-2">
            <BookOpenIcon className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">
              Все курсы
            </span>
            {!loading && total > 0 && (
              <span className="bg-gray-100 text-gray-500 text-xs font-medium px-2 py-0.5 rounded-full">
                {total}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* View toggle */}
            <div className="flex items-center bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setView("list")}
                title="Табличный вид"
                className={`p-1.5 rounded-md transition-all ${
                  view === "list"
                    ? "bg-white shadow-sm text-gray-700"
                    : "text-gray-400 hover:text-gray-600"
                }`}
              >
                <ListBulletIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => setView("grid")}
                title="Вид сеткой"
                className={`p-1.5 rounded-md transition-all ${
                  view === "grid"
                    ? "bg-white shadow-sm text-gray-700"
                    : "text-gray-400 hover:text-gray-600"
                }`}
              >
                <Squares2X2Icon className="w-4 h-4" />
              </button>
            </div>

            <button
              onClick={() => navigate("/courses/create")}
              className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3.5 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm"
            >
              <PlusIcon className="w-4 h-4" />
              Добавить курс
            </button>
          </div>
        </div>

        {/* ── SKELETON ── */}
        {loading && view === "grid" && (
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="border rounded-xl overflow-hidden animate-pulse">
                <div className="h-40 bg-gray-100" />
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-gray-100 rounded w-3/4" />
                  <div className="h-3 bg-gray-100 rounded w-full" />
                  <div className="h-3 bg-gray-100 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        )}

        {loading && view === "list" && (
          <div className="divide-y">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="px-5 py-4 flex items-center gap-4 animate-pulse">
                <div className="w-20 h-12 rounded-lg bg-gray-100 flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-100 rounded w-48" />
                  <div className="h-3 bg-gray-100 rounded w-72" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── EMPTY ── */}
        {!loading && courses.length === 0 && (
          <div className="py-20 text-center">
            <BookOpenIcon className="w-10 h-10 text-gray-200 mx-auto mb-3" />
            <p className="text-sm text-gray-400">Курсов пока нет</p>
            <button
              onClick={() => navigate("/courses/create")}
              className="mt-4 inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline"
            >
              <PlusIcon className="w-4 h-4" />
              Создать первый курс
            </button>
          </div>
        )}

        {/* ── GRID VIEW ── */}
        {!loading && view === "grid" && courses.length > 0 && (
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {courses.map((c) => (
              <div
                key={c.id}
                onClick={() => navigate(`/courses/${c.id}`)}
                className="border border-gray-200 rounded-xl overflow-hidden hover:border-blue-300 hover:shadow-sm transition-all group cursor-pointer"
              >
                {/* Cover */}
                <div className="relative h-40 bg-gray-50 overflow-hidden">
                  {withHttps(c.cover_image) ? (
                    <img
                      src={withHttps(c.cover_image)!}
                      alt={c.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <CoursePlaceholder />
                  )}

                  {(c.is_promoted || c.is_active === false) && (
                    <div className="absolute top-2 left-2 flex items-center gap-1.5">
                      {c.is_active === false && <InactiveBadge />}
                      {c.is_promoted && <PromotedBadge />}
                    </div>
                  )}
                </div>

                {/* Body */}
                <div className="p-4 space-y-2.5">
                  <h3 className="font-semibold text-gray-800 leading-snug line-clamp-1">
                    {c.title}
                  </h3>

                  {c.description && (
                    <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">
                      {c.description}
                    </p>
                  )}

                  <div className="flex items-center justify-between pt-1">
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <LanguageIcon className="w-3.5 h-3.5" />
                        {c.language.toUpperCase()}
                      </span>
                      {c.duration_minutes && (
                        <span className="flex items-center gap-1">
                          <ClockIcon className="w-3.5 h-3.5" />
                          {c.duration_minutes} мин
                        </span>
                      )}
                    </div>
                    <PriceBadge type={c.price.type} amount={c.price.amount} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── LIST VIEW ── */}
        {!loading && view === "list" && courses.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50/40">
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Курс
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    <span className="flex items-center gap-1.5">
                      <LanguageIcon className="w-3.5 h-3.5" />
                      Язык
                    </span>
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    <span className="flex items-center gap-1.5">
                      <ClockIcon className="w-3.5 h-3.5" />
                      Длительность
                    </span>
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Цена
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {courses.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/courses/${c.id}`)}
                    className="hover:bg-gray-50/60 transition-colors group cursor-pointer"
                  >
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-16 h-10 rounded-lg overflow-hidden bg-gray-50 flex-shrink-0 border border-gray-100">
                          {withHttps(c.cover_image) ? (
                            <img
                              src={withHttps(c.cover_image)!}
                              alt={c.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <CoursePlaceholder />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-800">
                              {c.title}
                            </span>
                            {c.is_promoted && <PromotedBadge />}
                            {c.is_active === false && <InactiveBadge />}
                          </div>
                          {c.description && (
                            <p className="text-xs text-gray-400 line-clamp-1 mt-0.5">
                              {c.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-gray-500 uppercase text-xs font-medium">
                      {c.language}
                    </td>
                    <td className="px-5 py-3.5 text-gray-500">
                      {c.duration_minutes ? (
                        `${c.duration_minutes} мин`
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      <PriceBadge type={c.price.type} amount={c.price.amount} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* PAGINATION */}
        {!loading && total > 0 && (
          <div className="flex items-center justify-between px-5 py-3.5 border-t bg-gray-50/40">
            <span className="text-xs text-gray-400">
              Страница{" "}
              <span className="font-medium text-gray-600">{currentPage}</span>{" "}
              из{" "}
              <span className="font-medium text-gray-600">
                {totalPages || 1}
              </span>
            </span>
            <div className="flex gap-1.5">
              <button
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - LIMIT))}
                className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                ← Назад
              </button>
              <button
                disabled={offset + LIMIT >= total}
                onClick={() => setOffset(offset + LIMIT)}
                className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Вперёд →
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}
